import { Injectable, signal } from '@angular/core';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../gt2/api/auth-service.service';
import { SessionEventsService } from '../gt2/api/session-events.service';
import { LocalizationConfig } from '../localization/LocalizationConfig';

interface ReverbConfig {
  key: string;
  wsHost: string;
  wsPort: number;
  wssPort: number;
  apiBaseUrl: string;
}

export type ReverbConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

@Injectable({
  providedIn: 'root',
})

export class ReverbService {
  private echo: Echo | null = null;
  private aiChannelUserId: string | null = null;
  private hadConnected = false;

  public connectionState = signal<ReverbConnectionState>('disconnected');
  public isConnected = signal(false);
  /** Émis quand la connexion pusher REVIENT après une perte (gap-fill du chat). */
  public reconnected$ = new Subject<void>();
  public messages$ = new Subject<{
    role: 'user' | 'model';
    text: string;
    streaming?: boolean;
    done?: boolean;
  }>();

  constructor(
    private authService: AuthenticationService,
    private sessionEvents: SessionEventsService,
  ) {
    // Teardown au logout — même pattern que GT2FirebaseService.
    this.sessionEvents.logout$.subscribe(() => this.disconnect());
  }

  /**
   * Instance Echo PARTAGÉE (un seul websocket pour le widget IA et le chat
   * d'équipe). Crée la connexion à la demande, sans abonner de channel.
   */
  public ensureConnected(): Echo | null {
    if (typeof window === 'undefined') return null;
    if (this.echo) return this.echo;

    this.connectionState.set('connecting');

    try {
      (window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher;
      const config = this.reverbConfig;
      const token = this.authService.getToken();
      const authEndpoint = `${config.apiBaseUrl}/broadcasting/auth`;

      this.echo = new Echo({
        broadcaster: 'pusher',
        key: config.key,
        wsHost: config.wsHost,
        wsPort: config.wsPort,
        wssPort: config.wssPort,
        forceTLS: true,
        encrypted: true,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        cluster: 'mt1',
        authEndpoint,
        auth: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      });

      const connector = this.echo.connector as { pusher?: { connection: Pusher['connection'] } };
      if (connector?.pusher) {
        const connection = connector.pusher.connection;
        connection.bind('connected', () => {
          this.connectionState.set('connected');
          this.isConnected.set(true);
          if (this.hadConnected) {
            this.reconnected$.next();
          }
          this.hadConnected = true;
        });
        connection.bind('error', (err: unknown) => {
          console.error('[Reverb] Pusher error', err);
          this.connectionState.set('error');
          this.isConnected.set(false);
        });
        connection.bind('unavailable', () => {
          this.connectionState.set('error');
          this.isConnected.set(false);
        });
        connection.bind('disconnected', () => {
          this.connectionState.set('disconnected');
          this.isConnected.set(false);
        });
      } else {
        console.warn('[Reverb] No pusher connector found on Echo instance');
      }
    } catch (err) {
      console.error('[Reverb] Init error', err);
      this.connectionState.set('error');
      this.echo = null;
    }

    return this.echo;
  }

  private get reverbConfig(): ReverbConfig {
    const rev = (environment as { reverb?: ReverbConfig }).reverb;
    return (
      rev ?? {
        key: 'laravel-herd',
        wsHost: environment.api.baseUrl.replace('https://api.', 'ws.'),
        wsPort: 443,
        wssPort: 443,
        apiBaseUrl: environment.api.baseUrl,
      }
    );
  }

  public connectToChat(userId: string): void {
    const echo = this.ensureConnected();
    if (!echo) return;

    if (this.aiChannelUserId === userId) return; // déjà abonné
    if (this.aiChannelUserId !== null) {
      echo.leave(`ai-chat.${this.aiChannelUserId}`);
    }
    this.aiChannelUserId = userId;

    const channelName = `ai-chat.${userId}`;
    echo
      .private(channelName)
      .listen('.stream_start', () => {
        // no-op
      })
      .listen('.text_delta', (e: { delta: string }) => {
        this.messages$.next({ role: 'model', text: e.delta, streaming: true });
      })
      .listen('.stream_end', () => {
        this.messages$.next({ role: 'model', text: '', streaming: false, done: true });
      });
  }

  async sendMessage(
    message: string,
    conversationId?: string,
    provider?: string,
  ): Promise<{ conversation_id: string }> {
    const config = this.reverbConfig;
    const token = this.authService.getToken();
    const lang = LocalizationConfig.getCurrentLanguage().locale;

    const body: Record<string, string> = { message };
    if (conversationId) body['conversation_id'] = conversationId;
    if (provider) body['provider'] = provider;

    try {
      const response = await fetch(`${config.apiBaseUrl}/v1/chat/ai-send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Accept-Language': lang,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
      }
      return response.json();
    } catch (error) {
      console.error('Reverb API Error', error);
      throw error;
    }
  }

  async loadHistory(conversationId?: string): Promise<{
    conversation_id: string | null;
    messages: { role: string; content: string; created_at: string }[];
  }> {
    const config = this.reverbConfig;
    const token = this.authService.getToken();
    const lang = LocalizationConfig.getCurrentLanguage().locale;
    const params = conversationId ? `?conversation_id=${conversationId}` : '';

    try {
      console.log('[Reverb] loadHistory', { conversationId, url: `${config.apiBaseUrl}/v1/chat/ai-history${params}` });
      const response = await fetch(`${config.apiBaseUrl}/v1/chat/ai-history${params}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Accept-Language': lang,
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
      }
      return response.json();
    } catch (error) {
      console.error('Reverb loadHistory Error', error);
      return { conversation_id: null, messages: [] };
    }
  }

  public disconnect(): void {
    if (this.echo) {
      this.echo.disconnect();
      this.echo = null;
      this.aiChannelUserId = null;
      this.hadConnected = false;
      this.isConnected.set(false);
      this.connectionState.set('disconnected');
    }
  }
}
